import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { LeaseStatusEnum } from '@app/core/enums/asset-occupant/lease-status-enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Lease, LeaseInput } from '@app/core/model/entities/asset/lease';
import { Occupant, OccupantInput } from '@app/core/model/entities/asset/occupant';
import { Document } from '@app/core/model/entities/document/document';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { DocumentsService } from '@app/features/main/views/organization-documents/documents.service';
import { eq, inSet } from '@app/shared/extra/filter-model-builder';
import { getLocale, thenReturn } from '@app/shared/extra/utils';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { combineLatest, EMPTY, expand, from, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, reduce, switchMap, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class OccupantsService {
  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private documentsService = inject(DocumentsService);
  private assetsService = inject(AssetsService);
  private appConfig = inject(AppConfig);
  private sidePanelToggleSubject = new Subject<Lease | null>();

  private addLeaseSubject = new Subject<Lease>();
  private updateOccupantSubject = new Subject<Occupant>();
  private updateLeaseSubject = new Subject<Lease>();
  private renewLeaseSubject = new Subject<{ renewedLease: Lease, oldLease: Lease }>();
  private deleteLeasesSubject = new Subject<Lease[]>();

  private readonly leaseInfoGraphQlFragment = gql`
    fragment LeaseInfo on Lease {
      id
      assetId
      occupantId
      spaceIdsList
      status
      properties
      computedProperties
      creationDate
      creationActor {
        actorId
        name
      }
      lastChangeDate
      lastChangeActor {
        actorId
        name
      }
      dataDate
    }
  `;

  private readonly occupantInfoGraphQlFragment = gql`
    fragment OccupantInfo on Occupant {
      id
      name
      identifier
      organizationId
      properties
      computedProperties
      creationDate
      creationActor {
        actorId
        name
      }
      lastChangeDate
      lastChangeActor {
        actorId
        name
      }
      dataDate
    }
  `;


  /**
   * Loads a leases page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch leases based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Lease objects.
   */
  public loadLeasesPage(selectParams: SelectParams): Observable<Lease[]> {
    const query = gql`
      query Leases($organizationId: String!, $selectParams: SelectParams!) {
        leases(organizationId: $organizationId, selectParams: $selectParams) {
          ...LeaseInfo
        }
      }
      ${this.leaseInfoGraphQlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Lease, data['leases'] as Lease[])));
  }

  /**
   * Loads all leases pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch leases based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Lease objects.
   */
  public loadAllLeases(selectParams: SelectParams): Observable<Lease[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadLeasesPage(params)
      .pipe(
        expand((leases, i) => {
          if (leases.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadLeasesPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Lease[])
      );
  }

  /**
   * Loads a tenants page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch tenants based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Occupant objects.
   */
  public loadOccupantsPage(selectParams: SelectParams): Observable<Occupant[]> {
    const query = gql`
      query Occupants($organizationId: String!, $selectParams: SelectParams!) {
        occupants(organizationId: $organizationId, selectParams: $selectParams) {
          ...OccupantInfo
        }
      }
      ${this.occupantInfoGraphQlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Occupant, data['occupants'] as Occupant[])));
  }

  /**
   * Loads all tenants pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch tenants based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Occupant objects.
   */
  public loadAllOccupants(selectParams: SelectParams): Observable<Occupant[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadOccupantsPage(params)
      .pipe(
        expand((occupants, i) => {
          if (occupants.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadOccupantsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Occupant[])
      );
  }

  /**
   * Same behavior as {@link loadLeasesPage} with an additional API query to link tenant data to each lease.
   * @param selectParams he parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return  An Observable emitting an array of Leases objects with tenant data linked.
   */
  public loadLeasesPageWithOccupantData(selectParams: SelectParams) {
    return combineLatest([
      this.loadLeasesPage(
        {
          ...selectParams,
          locale: getLocale(),
          sorts: [{field: 'startDate', direction: 'ASC'}, ...(selectParams.sorts ?? [])],
        }
      ),
      this.loadAllOccupants({}).pipe(
        // Transform occupant array into a dictionary for quick lookup
        map(occupants => {
          return occupants.reduce((acc, occupant) => {
            acc[occupant.id] = occupant;
            return acc;
          }, {} as Record<'id', Occupant>);
        })
      )
    ])
      .pipe(
        switchMap(([leases, occupants]) => {
          return from(leases).pipe(
            // Assign lease occupant and occupant leases list
            tap(lease => lease.occupant = occupants[lease.occupantId]),
            toArray()
          );
        })
      );
  }

  /**
   * Loads all leases pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch leases based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   * It also fetches information about leases' tenants.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Lease objects, each with additional tenant information.
   */
  public loadAllLeasesWithOccupantData(selectParams: SelectParams): Observable<Lease[]> {
    return combineLatest([
      this.loadAllLeases(
        {
          ...selectParams,
          locale: getLocale(),
          sorts: [{field: 'startDate', direction: 'ASC'}, ...(selectParams.sorts ?? [])],
        }
      ),
      this.loadAllOccupants({}).pipe(
        // Transform occupant array into a dictionary for quick lookup
        map(occupants => {
          return occupants.reduce((acc, occupant) => {
            acc[occupant.id] = occupant;
            return acc;
          }, {} as Record<'id', Occupant>);
        })
      )
    ])
      .pipe(
        switchMap(([leases, occupants]) => {
          return from(leases).pipe(
            // Assign lease occupant and occupant leases list
            tap(lease => lease.occupant = occupants[lease.occupantId]),
            toArray()
          );
        })
      );
  }

  /**
   * Emits Lease once it has been created.
   */
  public get leaseAdded$(): Observable<Lease> {
    return this.addLeaseSubject.asObservable();
  }

  /**
   * Emits an Occupant whenever it has been updated.
   */
  public get occupantUpdated$(): Observable<Occupant> {
    return this.updateOccupantSubject.asObservable();
  }

  /**
   * Emits a Lease whenever it has been updated.
   */
  public get leaseUpdated$(): Observable<Lease> {
    return this.updateLeaseSubject.asObservable();
  }

  /**
   * After a Lease is renewed, emits the old Lease and the new one.
   */
  public get leaseRenewed$(): Observable<{ renewedLease: Lease, oldLease: Lease }> {
    return this.renewLeaseSubject.asObservable();
  }

  /**
   * Emits a list of Leases once they have been deleted.
   */
  public get leasesDeleted$(): Observable<Lease[]> {
    return this.deleteLeasesSubject.asObservable();
  }

  /**
   * Emits a Lease whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Lease | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Call the API to create a new Occupant.
   * @param occupantInput New Occupant's data.
   * @returns Observable emitting the created Occupant.
   */
  public createOccupant(occupantInput: OccupantInput): Observable<Occupant> {
    const mutation = gql`
      mutation CreateOccupant($occupantInput: CreateOccupantInput!) {
        createOccupant(occupantInput: $occupantInput) {
          ...OccupantInfo
        }
      }
      ${this.occupantInfoGraphQlFragment}
    `;
    const variables = {
      occupantInput: {
        ...occupantInput,
        organizationId: this.appManager.currentOrganization.id
      },
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Occupant, data['createOccupant'])),
      );
  }

  /**
   * Call the API to create a new Lease for an Occupant and refresh the Occupant's data.
   * @param leaseInput New Lease's data.
   * @returns Observable emitting the created Lease.
   */
  public createLease(leaseInput: LeaseInput): Observable<Lease> {
    const mutation = gql`
      mutation CreateLease($leaseInput: CreateLeaseInput!) {
        createLease(leaseInput: $leaseInput) {
          ...LeaseInfo
        }
      }
      ${this.leaseInfoGraphQlFragment}
    `;
    const variables = {leaseInput};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Lease, data['createLease'])),
        switchMap(lease => {
          return this.loadOccupantsPage({filters: [eq('_id', lease.occupantId)]}).pipe(
            map(occupants => plainToInstance(Occupant, occupants[0])),
            tap(occupant => {
              this.updateOccupantSubject.next(occupant);
              lease.occupant = occupant;
            }),
            thenReturn(lease)
          );
        }),
        tap(lease => this.addLeaseSubject.next(lease))
      );
  }

  /**
   * Call the API to update an Occupant.
   * @param occupant Occupant to update.
   * @param occupantInput Data for updating the Occupant.
   * @return Updated Occupant.
   */
  public updateOccupant(occupant: Occupant, occupantInput: OccupantInput): Observable<Occupant> {
    const mutation = gql`
      mutation UpdateOccupant($occupantInput: UpdateOccupantInput!) {
        updateOccupant(occupantInput: $occupantInput) {
          ...OccupantInfo
        }
      }
      ${this.occupantInfoGraphQlFragment}
    `;
    const variables = {
      occupantInput: {
        id: occupant.id,
        name: occupant.name,
        ...occupantInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Occupant, data['updateOccupant'])),
        tap(updatedOccupant => this.updateOccupantSubject.next(updatedOccupant))
      );
  }

  /**
   * Call the API to update a Lease.
   * @param lease Lease to update.
   * @param leaseInput Data for updating the Lease.
   * @return Updated Lease.
   */
  public updateLease(lease: Lease, leaseInput: LeaseInput): Observable<Lease> {
    const mutation = gql`
      mutation UpdateLease($leaseInput: UpdateLeaseInput!){
        updateLease(leaseInput: $leaseInput){
          ...LeaseInfo
        }
      }
      ${this.leaseInfoGraphQlFragment}
    `;
    const variables = {
      leaseInput: {
        id: lease.id,
        spaceIds: lease.spaceIds,
        ...leaseInput
      }
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Lease, data['updateLease'])),
        tap(updatedLease => {
          updatedLease.occupant = lease.occupant;
          this.updateLeaseSubject.next(updatedLease);
        })
      );
  }

  /**
   * Renew an existing Lease.
   * @param leaseToRenew Lease to be renewed.
   * @param spaceIds New Lease's Spaces.
   * @return Observable that will emit the new Lease once created.
   */
  public renewLease(leaseToRenew: Lease, spaceIds: string[]): Observable<Lease> {
    const mutation = gql`
      mutation RenewLease($leaseInput: CreateLeaseInput!, $oldLeaseId: String!){
        renewLease(leaseInput: $leaseInput, id: $oldLeaseId){
          ...LeaseInfo
        }
      }
      ${this.leaseInfoGraphQlFragment}
    `;
    const variables = {
      leaseInput: {
        spaceIds,
        assetId: leaseToRenew.assetId,
        occupantId: leaseToRenew.occupant.id,
      },
      oldLeaseId: leaseToRenew.id
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Lease, data['renewLease'])),
        tap(renewedLease => {
          leaseToRenew.status = LeaseStatusEnum.LEASE_CLOSED;
          renewedLease.occupant = leaseToRenew.occupant;
          this.renewLeaseSubject.next({renewedLease: renewedLease, oldLease: leaseToRenew});
        })
      );
  }

  /**
   * Make an API call to delete the given Occupant.
   * @param occupantId Occupant's ID.
   * @return Observable emitting true if the Occupant was successfully deleted, false otherwise.
   */
  private deleteOccupant(occupantId: string): Observable<boolean> {
    const mutation = gql`
      mutation DeleteOccupant($occupantId: String!) {
        deleteOccupant(id: $occupantId)
      }
    `;
    const variables = {occupantId};
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteOccupant'] as boolean),
      );
  }

  /**
   * Make an API call to delete the given Leases. If any Occupants are left with no leases after the deletion,
   * then also delete them.
   * @param leases Leases to be deleted.
   * @return True if all Leases were deleted, false otherwise.
   */
  public deleteLeases(leases: Lease[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteLeases($leaseIds: [String!]) {
        deleteLeases(ids: $leaseIds)
      }
    `;
    const variables = {
      leaseIds: leases.map(lease => lease.id)
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteLeases'] as boolean),
        tap(success => {
          if (success) {
            this.deleteLeasesSubject.next(leases);
          }
        }),
        // Delete occupants left with no leases
        mergeMap(success => {
          const affectedOccupantIds = new Set(leases.map(lease => lease.occupantId));

          return this.loadAllLeases({
            groups: ['occupantId'],
            filters: [inSet('occupantId', [...affectedOccupantIds])]
          }).pipe(
            map(leasesGroups =>
              affectedOccupantIds.difference(new Set(leasesGroups.map(lease => lease.occupantId)))
            ),
            mergeMap(occupantIds => from(occupantIds)),
            mergeMap(occupantId => this.deleteOccupant(occupantId)),
            thenReturn(success)
          );
        })
      );
  }

  /**
   * Navigate to the occupants tab of the Asset's sheet.
   * @param assetId Id of the Asset to navigate to.
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'occupants');
  }

  /**
   * Load all the Documents related to a given Lease.
   * @param leaseId id of the selected Lease.
   * @return Observable emitting Documents linked to the Lease.
   */
  public loadLeaseDocuments(leaseId: string): Observable<Document[]> {
    return this.documentsService.loadEntityDocuments(
      leaseId,
      EntityTypeEnum.LEASE,
      DocumentType.DOCUMENT
    );
  }

  /**
   * Open the sidenav with information about the selected Lease and its related Occupant.
   * @param lease Lease to be displayed in the side panel.
   */
  public openLeaseSidePanel(lease: Lease): void {
    this.sidePanelToggleSubject.next(lease);
  }

  /**
   * Close the side panel.
   */
  public closeLeaseSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }
}
